import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useGoCardlessDropin } from '@gocardless/react-dropin'
import { Checkbox } from '../components/Checkbox'
import { FormNavigation } from '../components/FormNavigation'
import { InfoBox } from '../components/InfoBox'
import { submissionService, sepaService } from '../services/api'
import { reportError } from '../services/errorReporter'
import { logFlowEvent } from '../services/flowEventService'
import { buildSubmissionPayload } from '../utils/submissionPayload'
import { useOnboardingStore } from '../store/onboardingStore'
import { useTenant } from '../context/TenantContext'
import { useTestMode } from '../context/TestModeContext'
import { toast } from 'sonner'
import { Check, AlertTriangle, ShieldCheck } from 'lucide-react'
import { logger } from '../utils/logger'
import { sepaExperience, SEPA_OPT_OUT_REASONS, type SepaOptOutReason } from '../flow/sepaExperiments'
import type { StepProps } from '../types'

interface SepaStepProps extends StepProps {
  /** false: the patient's submission already exists (psychotherapy returning patients) — none is created */
  createSubmission?: boolean
  /**
   * Runs after the submission is stored and BEFORE any mandate page (also in test mode): the
   * psychotherapy variant completes its case here, so leaving for GoCardless loses nothing
   * (incident 2026-10-09). false keeps the patient on this step.
   */
  beforeMandate?: (optOut: boolean) => Promise<boolean>
  /** This visit is already stored (e.g. the psychotherapy case is complete): offer "set up now / later" */
  alreadyStored?: boolean
}

export function Step11SepaDebit({ onNext, onBack, createSubmission = true, beforeMandate, alreadyStored = false }: SepaStepProps) {
  const { t } = useTranslation()
  const { tenant } = useTenant()
  const [optOut, setOptOut] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  // Only the org's own sepa_redirect_url: the old build-time fallback (VITE_SEPA_REDIRECT_URL) put a
  // Make hook URL into the public bundle (removed after the Railway incident, 2026-10-23)
  const sepaRedirectUrl = tenant?.sepaRedirectUrl || null
  const {
    patientNumber,
    personalData,
    setSepaOptOut,
    setSepaDropinCompleted,
  } = useOnboardingStore()

  const isTestMode = useTestMode()

  // Embedded GoCardless Drop-in or the redirect, and the one- or two-step opt-out — per org, and
  // 50/50 by patient number while an A/B test runs (src/flow/sepaExperiments.ts). The legacy
  // full-page redirect stays as fallback (docs/sepa-dropin.md).
  const experience = sepaExperience({
    sepaDropin: tenant?.sepaDropin,
    sepaOptOutVariant: tenant?.sepaOptOutVariant,
    patientNumber,
    testMode: isTestMode,
  })
  const dropinEnabled = experience.dropin
  const twoStepOptOut = experience.twoStepOptOut
  const [optOutReason, setOptOutReason] = useState<SepaOptOutReason | null>(null)
  const [flow, setFlow] = useState<{ id: string; env: 'live' | 'sandbox' } | null>(null)
  const [abandoned, setAbandoned] = useState(false)
  // Returning patient: the submission already exists (this tab redirected to
  // the external SEPA flow earlier, or another browser/device did). Show a
  // way forward instead of the full form again — the restart loop costs a
  // second signature request every time.
  const [returned, setReturned] = useState(false)
  useEffect(() => {
    if (isTestMode || !/^P\d+$/.test(patientNumber)) return
    if (alreadyStored) { setReturned(true); return }
    // Without a submission of its own the earlier one says nothing about this visit
    if (!createSubmission) return
    if (sessionStorage.getItem('submission-sent') === patientNumber) { setReturned(true); return }
    submissionService.getStatus(patientNumber)
      .then(st => { if (st.returned) { sessionStorage.setItem('submission-sent', patientNumber); setReturned(true) } })
      .catch(() => { /* status is best-effort */ })
  }, [patientNumber, isTestMode, createSubmission, alreadyStored])

  /** The mandate is in place, seen right here — tell the stored submission (it was created before this step). */
  const confirmMandate = (via: 'dropin' | 'already_active') => {
    if (isTestMode) return
    sepaService.confirmMandate(patientNumber, via).catch(err => logger.warn('[SEPA] mandate confirmation not stored:', err))
  }

  /**
   * Hand the patient to the SEPA mandate flow. Asks the invoice system ONCE:
   * an already-active mandate skips the external page entirely (that page
   * only says "nothing to do", and a confused patient restarts the form);
   * a fresh flow goes straight to GoCardless; anything else uses the legacy
   * redirect. Never both — each proxy call mints a new GoCardless flow.
   */
  const goToSepa = async (): Promise<void> => {
    try {
      const result = await sepaService.getDropinFlow(patientNumber)
      if (result.status === 'already_active') {
        logFlowEvent(patientNumber, 'step11_sepa', 'step_completed')
        setSepaDropinCompleted(true)
        confirmMandate('already_active')
        toast.success(t('sepaDropin.alreadyActive'))
        onNext()
        return
      }
      if (result.authorizationUrl) {
        logFlowEvent(patientNumber, 'sepa_redirect', 'step_entered')
        window.location.href = result.authorizationUrl
        return
      }
    } catch (err) {
      logger.warn('[SEPA] flow lookup failed, using legacy redirect:', err)
    }
    if (sepaRedirectUrl) {
      logFlowEvent(patientNumber, 'sepa_redirect', 'step_entered')
      window.location.href = `${sepaRedirectUrl}?pid=${encodeURIComponent(patientNumber)}`
      return
    }
    onNext()
  }
  const openedRef = useRef(false)

  const { open: openDropin, ready: dropinReady } = useGoCardlessDropin({
    billingRequestFlowID: flow?.id ?? '',
    environment: flow?.env ?? 'live',
    onSuccess: (billingRequest) => {
      // Flow completed — the mandate activates asynchronously (1–2 business
      // days); our invoice system's webhooks take over from here. Optimistic
      // success is the documented pattern.
      logger.log('[SEPA Drop-in] success, billing request:', billingRequest.id)
      logFlowEvent(patientNumber, 'sepa_dropin', 'step_completed')
      setSepaDropinCompleted(true)
      confirmMandate('dropin')
      toast.success(t('sepaDropin.success'), { duration: 8000 })
      onNext()
    },
    onExit: (error) => {
      openedRef.current = false
      setFlow(null)
      if (error) {
        logger.error('[SEPA Drop-in] exit with error:', error)
        reportError({ patientNumber, errorType: 'sepa_dropin_error', errorMessage: JSON.stringify(error).slice(0, 500), step: 'step11' })
        logFlowEvent(patientNumber, 'sepa_dropin', 'error')
        toast.error(t('sepaDropin.error'))
      } else {
        toast.info(t('sepaDropin.abandoned'))
      }
      setAbandoned(true)
    },
  })

  // Open once the SDK is ready for the fetched flow
  useEffect(() => {
    if (flow && dropinReady && !openedRef.current) {
      openedRef.current = true
      logFlowEvent(patientNumber, 'sepa_dropin', 'step_entered')
      openDropin()
    }
  }, [flow, dropinReady, openDropin, patientNumber])

  /** Fetch a fresh Billing Request Flow and open the Drop-in (flows are single-use). */
  const startDropin = async (): Promise<void> => {
    if (!/^P\d+$/.test(patientNumber)) {
      // Typically: jumped here via the test navigator without step 1
      toast.error(t('sepaDropin.noPatientNumber'))
      return
    }
    const result = await sepaService.getDropinFlow(patientNumber)

    if (result.status === 'already_active') {
      setSepaDropinCompleted(true)
      confirmMandate('already_active')
      toast.success(t('sepaDropin.alreadyActive'))
      onNext()
      return
    }
    if (result.available && result.status === 'created' && result.billingRequestFlowId) {
      setFlow({ id: result.billingRequestFlowId, env: result.environment === 'sandbox' ? 'sandbox' : 'live' })
      return
    }

    // Drop-in not possible → documented fallbacks
    logger.warn('[SEPA Drop-in] unavailable:', result.reason)
    if (result.authorizationUrl) {
      window.location.href = result.authorizationUrl
    } else if (sepaRedirectUrl && !isTestMode) {
      window.location.href = `${sepaRedirectUrl}?pid=${encodeURIComponent(patientNumber)}`
    } else {
      toast.info(t('sepaDropin.unavailable'))
      onNext()
    }
  }

  /** The mandate step: the embedded Drop-in (falls back to the redirect internally) or the redirect. */
  const startMandate = async (): Promise<void> => {
    if (!dropinEnabled) {
      await goToSepa()
      return
    }
    try {
      await startDropin()
    } catch (err) {
      logger.error('[SEPA Drop-in] flow fetch failed, falling back to redirect:', err)
      if (sepaRedirectUrl) {
        window.location.href = `${sepaRedirectUrl}?pid=${encodeURIComponent(patientNumber)}`
        return
      }
      onNext()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await submit(optOut)
  }

  /** `optOutChoice` is passed in: "Doch per Lastschrift" submits right after unticking the opt-out. */
  const submit = async (optOutChoice: boolean) => {
    // In test mode, skip webhook/submission API calls — but DO exercise the
    // Drop-in when it's enabled, that's exactly what test mode is for
    if (isTestMode) {
      if (beforeMandate) {
        setIsLoading(true)
        const ok = await beforeMandate(optOutChoice).finally(() => setIsLoading(false))
        if (!ok) return
      }
      if (dropinEnabled && !optOutChoice) {
        setIsLoading(true)
        try {
          await startDropin()
        } catch (err) {
          logger.error('[SEPA Drop-in] test-mode flow fetch failed:', err)
          toast.error(t('sepaDropin.error'))
        } finally {
          setIsLoading(false)
        }
        return
      }
      toast.success(t('step11.testModeSkip'))
      onNext()
      return
    }

    setIsLoading(true)

    const submissionData = buildSubmissionPayload(useOnboardingStore.getState(), {
      sepaOptOut: optOutChoice,
      sepaVariant: experience.sepaVariant,
      sepaOptOutVariant: experience.sepaOptOutVariant,
      sepaOptOutReason: twoStepOptOut ? optOutReason : null,
    })

    setSepaOptOut(optOutChoice)

    // Store submission in database — backend also dispatches the org-scoped webhook
    // and dedupes per patient, so a re-mount (e.g. returning from the SEPA redirect)
    // can never create a duplicate.
    if (createSubmission && sessionStorage.getItem('submission-sent') !== patientNumber) {
      try {
        const submissionResult = await submissionService.create({
          ...submissionData,
          webhookSent: false,
        })
        if (submissionResult.success && submissionResult.data) {
          sessionStorage.setItem('submission-sent', patientNumber)
          logger.log('[Step11] Submission stored:', submissionResult.data.id)
        }
      } catch (dbError) {
        logger.error('[Step11] Failed to store submission:', dbError)
      }
    }

    if (beforeMandate && !(await beforeMandate(optOutChoice))) {
      setIsLoading(false)
      return
    }

    // Mandate setup based on opt-out status
    if (optOutChoice) {
      setIsLoading(false)
      onNext()
      return
    }
    try {
      await startMandate()
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="form-inner py-12">
        <h2 className="text-h2 text-foreground text-center mb-2">
          {t('step11.title')}
        </h2>
        <p className="text-body text-foreground text-center mb-2">
          {personalData.firstName} {personalData.lastName}
        </p>
        <p className="text-body text-muted-foreground text-center mb-8">
          {t('step11.subtitle')}
        </p>

        <div className="bg-white rounded-card p-6 mb-6">
          <h3 className="text-h3 text-foreground mb-4">{t('step11.whySepa')}</h3>
          <p className="text-body text-muted-foreground mb-6">
            {t('step11.sepaDescription')}
          </p>

          <h4 className="text-body font-medium mb-3">{t('step11.benefits')}</h4>
          <ul className="space-y-3 mb-6">
            {[
              { title: t('step11.benefitConvenient'), desc: t('step11.benefitConvenientDesc') },
              { title: t('step11.benefitReliable'), desc: t('step11.benefitReliableDesc') },
              { title: t('step11.benefitTimeSaving'), desc: t('step11.benefitTimeSavingDesc') },
              { title: t('step11.benefitSecure'), desc: t('step11.benefitSecureDesc') },
            ].map((item) => (
              <li key={item.title} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">{item.title}:</span>{' '}
                  <span className="text-muted-foreground">{item.desc}</span>
                </div>
              </li>
            ))}
          </ul>

          <div className="bg-green-50 border border-green-200 rounded-card p-4">
            <p className="text-small text-green-700">
              <strong>{t('step11.important')}</strong> {t('step11.importantNote')}
            </p>
          </div>

          {dropinEnabled && (
            <div className="flex items-start gap-2 mt-4 text-small text-muted-foreground">
              <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5 text-primary" />
              <p>{t('sepaDropin.trustNote')}</p>
            </div>
          )}
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-card p-6 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <h4 className="text-body font-medium text-yellow-800">
              {t('step11.optOutTitle')}
            </h4>
          </div>
          <p className="text-small text-yellow-700 mb-4">
            {t('step11.optOutDescription')}
          </p>

          <Checkbox
            id="optOut"
            checked={optOut}
            onChange={(e) => setOptOut(e.target.checked)}
            label={
              <span className="text-small text-yellow-800" dangerouslySetInnerHTML={{ __html: t('step11.optOut') }} />
            }
          />

          {/* Two-step opt-out (A/B test): ask why, answer that reason, offer SEPA once more. Never required. */}
          {twoStepOptOut && optOut && (
            <div className="mt-5 pt-4 border-t border-yellow-200" data-testid="sepa-optout-reasons">
              <p id="optout-reason-question" className="text-small font-medium text-yellow-800 mb-3">
                {t('step11.optOutReason.question')}
              </p>
              <div role="radiogroup" aria-labelledby="optout-reason-question" className="space-y-2">
                {SEPA_OPT_OUT_REASONS.map(reason => (
                  <label key={reason} className="flex items-start gap-2 text-small text-yellow-800 cursor-pointer">
                    <input
                      type="radio"
                      name="sepaOptOutReason"
                      value={reason}
                      checked={optOutReason === reason}
                      onChange={() => setOptOutReason(reason)}
                      className="mt-0.5 accent-primary"
                    />
                    <span>{t(`step11.optOutReason.${reason}`)}</span>
                  </label>
                ))}
              </div>
              {optOutReason && (
                <div className="bg-white border border-green-200 rounded-card p-4 mt-4" aria-live="polite">
                  <p className="text-small text-foreground mb-3">{t(`step11.optOutReason.answer.${optOutReason}`)}</p>
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => { setOptOut(false); void submit(false) }}
                    className="btn-primary"
                  >
                    {t('step11.optOutReason.useSepa')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {returned && !optOut && (
          <InfoBox className="mb-6">
            <p className="font-medium mb-1">{t('step11.returnedTitle')}</p>
            <p className="mb-3">{t('step11.returnedBody')}</p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={isLoading}
                onClick={async () => { setIsLoading(true); try { await startMandate() } finally { setIsLoading(false) } }}
                className="btn-primary"
              >
                {t('step11.setupNow')}
              </button>
              <button
                type="button"
                onClick={() => { logFlowEvent(patientNumber, 'step11_sepa', 'sepa_deferred'); onNext() }}
                className="text-primary underline text-body"
              >
                {t('step11.continueLater')}
              </button>
            </div>
          </InfoBox>
        )}

        {abandoned && (
          <InfoBox className="mb-6">
            <p className="mb-2">{t('sepaDropin.retryHint')}</p>
            <button
              type="button"
              onClick={() => { setAbandoned(false); onNext() }}
              className="text-primary underline text-body"
            >
              {t('sepaDropin.continueWithout')}
            </button>
          </InfoBox>
        )}

        <FormNavigation
          onBack={onBack}
          nextDisabled={isLoading}
          nextLabel={isLoading ? t('step11.submitting') : abandoned ? t('sepaDropin.retry') : twoStepOptOut && optOut ? t('step11.optOutReason.submitWithout') : t('common.submit')}
        />
      </form>
    </div>
  )
}
